import { useState, useEffect, useMemo } from 'react';
import { formatCurrency, parseAmount, generateId, formatAmountInput } from '../../utils/formatting';
import { formatDate } from '../../utils/date';

// 家事按分自動生成仕訳の識別子
const AUTO_APPORTIONMENT_DESCRIPTION = '家事按分（自動生成）';

export const ClosingWizard = ({ accounts, journals, setJournals, showAlert, currentFiscalYear, calculateBalances, accountType, fileSystem, deletedJournals, setDeletedJournals, orgType, fiscalYears }) => {
    const isKojin = accountType === 'kojin';
    const isCorp = !isKojin;
    const [activeTab, setActiveTab] = useState('inventory');

    const [beginningInventory, setBeginningInventory] = useState(0);
    const [endingInventory, setEndingInventory] = useState(0);

    const [personalUseRatios, setPersonalUseRatios] = useState({});
    const [isLoadingApportionment, setIsLoadingApportionment] = useState(false);

    const personalUseAccounts = useMemo(() => {
        const ratioAccountIds = Object.keys(personalUseRatios);
        return accounts.filter(a => ratioAccountIds.includes(a.id));
    }, [accounts, personalUseRatios]);

    const existingPersonalUseEntries = useMemo(() => {
        return journals.filter(j => j.closingType === 'personal-use');
    }, [journals]);

    const [apportionmentLoaded, setApportionmentLoaded] = useState(false);

    // 按分設定の読み込み（表示用）
    // 注意: 自動仕訳生成はApp.jsx起動時に実行される
    useEffect(() => {
        const loadApportionment = async () => {
            if (!isKojin || !fileSystem || !currentFiscalYear || apportionmentLoaded) return;

            setIsLoadingApportionment(true);

            try {
                const data = await fileSystem.getApportionment(currentFiscalYear);

                if (data && data.ratios) {
                    const ratios = {};
                    Object.entries(data.ratios).forEach(([accountId, setting]) => {
                        if (setting.businessRatio !== undefined && setting.businessRatio < 100) {
                            ratios[accountId] = setting.businessRatio;
                        }
                    });
                    setPersonalUseRatios(ratios);
                }

                setApportionmentLoaded(true);
            } catch (err) {
                console.error('Failed to load apportionment settings:', err);
                setApportionmentLoaded(true);
            } finally {
                setIsLoadingApportionment(false);
            }
        };

        loadApportionment();
    }, [isKojin, fileSystem, currentFiscalYear, apportionmentLoaded]);

    const getExpenseTotal = (accountId) => {
        return journals
            .filter(j => j.closingType !== 'personal-use')
            .flatMap(j => j.lines || [])
            .filter(l => l.accountId === accountId)
            .reduce((sum, l) => sum + (l.type === 'debit' ? l.amount : -l.amount), 0);
    };

    const getAccountTotal = (accountId) => {
        const balance = calculateBalances[accountId];
        if (!balance) return 0;
        const account = accounts.find(a => a.id === accountId);
        if (!account) return 0;
        return account.home === 'debit' ? balance.debit - balance.credit : balance.credit - balance.debit;
    };

    const registerInventoryEntries = () => {
        const entries = [];
        const inventoryAccountId = accounts.find(a => a.name === '繰越商品')?.id || '116';
        const purchaseAccountId = accounts.find(a => a.name === '仕入')?.id || '600';
        const closingDate = `${currentFiscalYear}-${isKojin ? '12' : '03'}-31`;

        if (beginningInventory > 0) {
            entries.push({
                id: generateId(),
                date: closingDate,
                description: '期首商品棚卸高',
                lines: [
                    { id: generateId(), type: 'debit', accountId: purchaseAccountId, amount: beginningInventory },
                    { id: generateId(), type: 'credit', accountId: inventoryAccountId, amount: beginningInventory }
                ],
                isClosingEntry: true,
                closingType: 'inventory',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        }
        if (endingInventory > 0) {
            entries.push({
                id: generateId(),
                date: closingDate,
                description: '期末商品棚卸高',
                lines: [
                    { id: generateId(), type: 'debit', accountId: inventoryAccountId, amount: endingInventory },
                    { id: generateId(), type: 'credit', accountId: purchaseAccountId, amount: endingInventory }
                ],
                isClosingEntry: true,
                closingType: 'inventory',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        }

        if (entries.length === 0) {
            showAlert('登録する仕訳がありません', 'warning');
            return;
        }

        setJournals([...journals, ...entries]);
        showAlert(`棚卸仕訳を${entries.length}件登録しました`);
        setBeginningInventory(0);
        setEndingInventory(0);
    };

    const registerPersonalUseEntries = () => {
        // 既存の家事按分仕訳（自動・手動含む）をチェック
        const currentPersonalUseEntries = journals.filter(j => j.closingType === 'personal-use');

        // 既存の自動生成仕訳を削除して再生成
        if (currentPersonalUseEntries.length > 0) {
            if (!confirm(`既存の家事按分仕訳（${currentPersonalUseEntries.length}件）を削除して再生成しますか？`)) {
                return;
            }

            // 既存仕訳を削除履歴に追加
            const deletedAt = new Date().toISOString();
            const deletedRecords = currentPersonalUseEntries.map(j => ({ ...j, deletedAt }));
            if (setDeletedJournals) {
                setDeletedJournals(prev => [...prev, ...deletedRecords]);
            }

            // 既存仕訳を削除
            const personalUseIds = new Set(currentPersonalUseEntries.map(j => j.id));
            setJournals(prev => prev.filter(j => !personalUseIds.has(j.id)));
        }

        const entries = [];
        const personalDrawingId = accounts.find(a => a.name === '事業主貸')?.id || '190';

        Object.entries(personalUseRatios).forEach(([accountId, ratio]) => {
            if (!ratio || ratio <= 0 || ratio >= 100) return;
            const total = getExpenseTotal(accountId);
            const personalAmount = Math.floor(total * (100 - ratio) / 100);
            if (personalAmount <= 0) return;

            const account = accounts.find(a => a.id === accountId);
            entries.push({
                id: generateId(),
                date: `${currentFiscalYear}-12-31`,
                description: `${AUTO_APPORTIONMENT_DESCRIPTION} ${account?.name || ''}`,
                lines: [
                    { id: generateId(), type: 'debit', accountId: personalDrawingId, amount: personalAmount },
                    { id: generateId(), type: 'credit', accountId, amount: personalAmount }
                ],
                isClosingEntry: true,
                closingType: 'personal-use',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        });

        if (entries.length === 0) {
            showAlert('登録する仕訳がありません（事業割合100%以外の科目を設定してください）', 'warning');
            return;
        }

        // 既存を削除した後に追加するため、setTimeoutで遅延
        setTimeout(() => {
            setJournals(prev => [...prev, ...entries]);
            showAlert(`家事按分仕訳を${entries.length}件${currentPersonalUseEntries.length > 0 ? '再' : ''}登録しました`);
        }, 100);
    };

    const deletePersonalUseEntries = () => {
        const currentPersonalUseEntries = journals.filter(j => j.closingType === 'personal-use');

        if (currentPersonalUseEntries.length === 0) {
            showAlert('削除する家事按分仕訳がありません', 'warning');
            return;
        }
        if (!confirm(`既存の家事按分仕訳（${currentPersonalUseEntries.length}件）を削除しますか？`)) {
            return;
        }

        const personalUseIds = new Set(currentPersonalUseEntries.map(j => j.id));

        const deletedAt = new Date().toISOString();
        const deletedRecords = currentPersonalUseEntries.map(j => ({ ...j, deletedAt }));
        if (setDeletedJournals) {
            setDeletedJournals(prev => [...prev, ...deletedRecords]);
        }

        setJournals(prev => prev.filter(j => !personalUseIds.has(j.id)));
        showAlert(`家事按分仕訳を${currentPersonalUseEntries.length}件削除しました`);
    };

    // 未払費用計上（法人用）
    const [accrualAmounts, setAccrualAmounts] = useState({
        corporateTax: 0,
        consumptionTax: 0,
        businessTax: 0
    });

    const getClosingDate = () => {
        if (isKojin) return `${currentFiscalYear}-12-31`;
        const currentFY = fiscalYears?.find(fy => fy.year === currentFiscalYear);
        const endMonth = currentFY?.endMonth || 3;
        const endYear = endMonth === 12 ? currentFiscalYear : currentFiscalYear + 1;
        const lastDay = new Date(endYear, endMonth, 0).getDate();
        return `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    };

    const registerAccrualEntries = () => {
        const entries = [];
        const closingDate = getClosingDate();

        const accrualItems = [
            { key: 'corporateTax', debitAccountName: '法人税等', creditAccountName: '未払法人税等', description: '未払法人税等の計上' },
            { key: 'consumptionTax', debitAccountName: '租税公課', creditAccountName: '未払消費税', description: '未払消費税の計上' },
            { key: 'businessTax', debitAccountName: '法人税等', creditAccountName: '未払事業税', description: '未払事業税の計上' }
        ];

        accrualItems.forEach(item => {
            const amount = Number(accrualAmounts[item.key]) || 0;
            if (amount <= 0) return;

            const debitAccount = accounts.find(a => a.name === item.debitAccountName);
            const creditAccount = accounts.find(a => a.name === item.creditAccountName);
            if (!debitAccount || !creditAccount) return;

            entries.push({
                id: generateId(),
                date: closingDate,
                description: item.description,
                lines: [
                    { id: generateId(), type: 'debit', accountId: debitAccount.id, amount },
                    { id: generateId(), type: 'credit', accountId: creditAccount.id, amount }
                ],
                isClosingEntry: true,
                closingType: 'accruals',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        });

        if (entries.length === 0) {
            showAlert('金額を入力してください', 'warning');
            return;
        }

        setJournals([...journals, ...entries]);
        showAlert(`未払費用の仕訳を${entries.length}件登録しました`);
        setAccrualAmounts({ corporateTax: 0, consumptionTax: 0, businessTax: 0 });
    };

    const existingClosingEntries = useMemo(() => {
        return journals.filter(j => j.isClosingEntry);
    }, [journals]);

    // 元入金振替用の計算（個人事業主のみ）
    const { netIncome, ownerWithdrawal, ownerDeposit } = useMemo(() => {
        if (!isKojin) return { netIncome: 0, ownerWithdrawal: 0, ownerDeposit: 0 };
        const ni = accounts.reduce((sum, a) => {
            const bal = calculateBalances[a.id];
            if (!bal) return sum;
            if (a.type === 'revenue' || a.home === 'credit' && a.statement === 'PL') {
                return sum + (bal.credit - bal.debit);
            }
            if (a.type === 'expense' || a.home === 'debit' && a.statement === 'PL') {
                return sum - (bal.debit - bal.credit);
            }
            return sum;
        }, 0);
        const withdrawalAcc = accounts.find(a => a.name.includes('事業主貸'));
        const depositAcc = accounts.find(a => a.name.includes('事業主借'));
        return {
            netIncome: ni,
            ownerWithdrawal: withdrawalAcc ? Math.max(0, getAccountTotal(withdrawalAcc.id)) : 0,
            ownerDeposit: depositAcc ? Math.max(0, getAccountTotal(depositAcc.id)) : 0,
        };
    }, [accounts, calculateBalances, isKojin]);

    const handleGenerateCapitalTransfer = () => {
        const capitalAccount = accounts.find(a => a.name.includes('元入金'));
        const withdrawalAccount = accounts.find(a => a.name.includes('事業主貸'));
        const depositAccount = accounts.find(a => a.name.includes('事業主借'));
        if (!capitalAccount) { showAlert('元入金科目が見つかりません', 'error'); return; }

        const filtered = journals.filter(j => j.closingType !== 'capital-transfer');
        const newEntries = [];
        const yearEnd = `${currentFiscalYear}-12-31`;

        if (netIncome !== 0) {
            newEntries.push({
                id: generateId(), date: yearEnd,
                description: '決算振替（損益→元入金）',
                lines: netIncome > 0
                    ? [{ id: generateId(), type: 'debit', accountId: capitalAccount.id, amount: netIncome },
                       { id: generateId(), type: 'credit', accountId: capitalAccount.id, amount: netIncome }]
                    : [{ id: generateId(), type: 'debit', accountId: capitalAccount.id, amount: -netIncome },
                       { id: generateId(), type: 'credit', accountId: capitalAccount.id, amount: -netIncome }],
                isClosingEntry: true, closingType: 'capital-transfer',
                createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
            });
        }
        if (ownerWithdrawal > 0 && withdrawalAccount) {
            newEntries.push({
                id: generateId(), date: yearEnd,
                description: '決算振替（事業主貸→元入金）',
                lines: [{ id: generateId(), type: 'debit', accountId: capitalAccount.id, amount: ownerWithdrawal },
                        { id: generateId(), type: 'credit', accountId: withdrawalAccount.id, amount: ownerWithdrawal }],
                isClosingEntry: true, closingType: 'capital-transfer',
                createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
            });
        }
        if (ownerDeposit > 0 && depositAccount) {
            newEntries.push({
                id: generateId(), date: yearEnd,
                description: '決算振替（事業主借→元入金）',
                lines: [{ id: generateId(), type: 'debit', accountId: depositAccount.id, amount: ownerDeposit },
                        { id: generateId(), type: 'credit', accountId: capitalAccount.id, amount: ownerDeposit }],
                isClosingEntry: true, closingType: 'capital-transfer',
                createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
            });
        }
        setJournals([...filtered, ...newEntries]);
        showAlert('振替仕訳を生成しました', 'success');
    };

    const tabStyle = (tabName) => ({
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === tabName ? '3px solid #3498db' : '3px solid transparent',
        background: activeTab === tabName ? '#fff' : '#f8f9fa',
        cursor: 'pointer',
        fontWeight: activeTab === tabName ? 'bold' : 'normal'
    });

    return (
        <div className="page-container">
            <h2>決算整理 <span className="year-badge">{currentFiscalYear}年{isKojin ? '' : '度'}</span></h2>

            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button style={tabStyle('inventory')} onClick={() => setActiveTab('inventory')}>棚卸</button>
                {isKojin && <button style={tabStyle('personal-use')} onClick={() => setActiveTab('personal-use')}>家事按分</button>}
                {isKojin && <button style={tabStyle('capital-transfer')} onClick={() => setActiveTab('capital-transfer')}>元入金振替</button>}
                {isCorp && <button style={tabStyle('accruals')} onClick={() => setActiveTab('accruals')}>未払費用計上</button>}
                <button style={tabStyle('registered')} onClick={() => setActiveTab('registered')}>登録済み</button>
            </div>

            {activeTab === 'inventory' && (
                <div className="card">
                    <h3>棚卸高の入力</h3>
                    <p style={{ color: '#666', marginBottom: '16px' }}>商品・製品の棚卸を行っている場合のみ入力してください</p>

                    <div className="form-group">
                        <label>期首商品棚卸高</label>
                        <input
                            type="text"
                            className="form-control"
                            value={formatAmountInput(beginningInventory)}
                            onChange={e => setBeginningInventory(parseAmount(e.target.value))}
                            placeholder="0"
                            style={{ width: '200px', textAlign: 'right', fontSize: '16px' }}
                        />
                        <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>前年度末の在庫金額</p>
                    </div>

                    <div className="form-group">
                        <label>期末商品棚卸高</label>
                        <input
                            type="text"
                            className="form-control"
                            value={formatAmountInput(endingInventory)}
                            onChange={e => setEndingInventory(parseAmount(e.target.value))}
                            placeholder="0"
                            style={{ width: '200px', textAlign: 'right', fontSize: '16px' }}
                        />
                        <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>今年度末の在庫金額</p>
                    </div>

                    <div style={{ marginTop: '24px' }}>
                        <button
                            className="btn btn-primary"
                            onClick={registerInventoryEntries}
                            disabled={beginningInventory === 0 && endingInventory === 0}
                        >
                            棚卸仕訳を登録
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'personal-use' && isKojin && (
                <div className="card">
                    <h3>家事按分</h3>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                        事業で使用した割合（%）を入力してください。100%以外の科目について仕訳が作成されます。<br />
                        <span style={{ fontSize: '13px' }}>※ 「データ管理」→「家事按分設定」で設定した内容が自動反映されています。</span>
                    </p>

                    {existingPersonalUseEntries.length > 0 && (
                        <div style={{
                            padding: '16px',
                            marginBottom: '20px',
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '8px'
                        }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#856404' }}>
                                既存の家事按分仕訳があります（{existingPersonalUseEntries.length}件）
                            </p>
                            <p style={{ fontSize: '13px', color: '#856404', marginBottom: '12px' }}>
                                再生成する場合は、先に既存の仕訳を削除してください。
                            </p>
                            <button
                                className="btn btn-danger btn-sm"
                                onClick={deletePersonalUseEntries}
                            >
                                既存の按分仕訳を削除
                            </button>
                        </div>
                    )}

                    {isLoadingApportionment ? (
                        <p style={{ textAlign: 'center', padding: '40px' }}>設定を読み込み中...</p>
                    ) : personalUseAccounts.length === 0 ? (
                        <div style={{
                            padding: '40px',
                            textAlign: 'center',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            color: '#666'
                        }}>
                            <p style={{ marginBottom: '12px' }}>家事按分の設定がありません。</p>
                            <p style={{ fontSize: '13px' }}>
                                「データ管理」→「家事按分設定」タブで按分する科目を追加してください。
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>科目</th>
                                        <th style={{ textAlign: 'right' }}>年間合計</th>
                                        <th style={{ width: '120px' }}>事業割合</th>
                                        <th style={{ textAlign: 'right' }}>経費計上額</th>
                                        <th style={{ textAlign: 'right' }}>家事分</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {personalUseAccounts.map(account => {
                                        const total = getExpenseTotal(account.id);
                                        const ratio = personalUseRatios[account.id] ?? 100;
                                        const businessAmount = Math.floor(total * ratio / 100);
                                        const personalAmount = total - businessAmount;
                                        return (
                                            <tr key={account.id} style={ratio < 100 ? { background: '#f8f9fa' } : {}}>
                                                <td>{account.name}</td>
                                                <td style={{ textAlign: 'right' }}>{formatCurrency(total)}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={ratio}
                                                            onChange={e => setPersonalUseRatios({ ...personalUseRatios, [account.id]: parseInt(e.target.value) || 0 })}
                                                            min="0" max="100"
                                                            style={{ width: '100px', textAlign: 'right', fontSize: '16px' }}
                                                        />
                                                        <span>%</span>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{formatCurrency(businessAmount)}</td>
                                                <td style={{ textAlign: 'right', color: personalAmount > 0 ? '#e74c3c' : '#999' }}>
                                                    {formatCurrency(personalAmount)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            <div style={{ marginTop: '24px' }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={registerPersonalUseEntries}
                                    disabled={
                                        existingPersonalUseEntries.length > 0 ||
                                        Object.values(personalUseRatios).every(r => r === undefined || r >= 100)
                                    }
                                >
                                    家事按分仕訳を登録
                                </button>
                                {existingPersonalUseEntries.length > 0 && (
                                    <span style={{ marginLeft: '12px', color: '#666', fontSize: '13px' }}>
                                        ※ 既存の仕訳を削除してから登録してください
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'accruals' && isCorp && (
                <div className="card">
                    <h3>未払費用の計上</h3>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                        決算時に確定した税額を入力してください。仕訳が自動生成されます。
                    </p>

                    <div className="form-group">
                        <label>未払法人税等</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                className="form-control"
                                value={formatAmountInput(accrualAmounts.corporateTax)}
                                onChange={e => setAccrualAmounts(prev => ({ ...prev, corporateTax: parseAmount(e.target.value) }))}
                                style={{ width: '200px', textAlign: 'right', fontSize: '16px' }}
                            />
                            <span style={{ color: '#666', fontSize: '13px' }}>法人税等 / 未払法人税等</span>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>未払消費税</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                className="form-control"
                                value={formatAmountInput(accrualAmounts.consumptionTax)}
                                onChange={e => setAccrualAmounts(prev => ({ ...prev, consumptionTax: parseAmount(e.target.value) }))}
                                style={{ width: '200px', textAlign: 'right', fontSize: '16px' }}
                            />
                            <span style={{ color: '#666', fontSize: '13px' }}>租税公課 / 未払消費税</span>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>未払事業税</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                className="form-control"
                                value={formatAmountInput(accrualAmounts.businessTax)}
                                onChange={e => setAccrualAmounts(prev => ({ ...prev, businessTax: parseAmount(e.target.value) }))}
                                style={{ width: '200px', textAlign: 'right', fontSize: '16px' }}
                            />
                            <span style={{ color: '#666', fontSize: '13px' }}>法人税等 / 未払事業税</span>
                        </div>
                    </div>

                    <div style={{ marginTop: '24px' }}>
                        <button
                            className="btn btn-primary"
                            onClick={registerAccrualEntries}
                            disabled={!accrualAmounts.corporateTax && !accrualAmounts.consumptionTax && !accrualAmounts.businessTax}
                        >
                            未払費用仕訳を登録
                        </button>
                    </div>

                    <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#666' }}>
                        <p>生成される仕訳:</p>
                        <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                            <li>法人税等: 借方「法人税等」/ 貸方「未払法人税等」</li>
                            <li>消費税: 借方「租税公課」/ 貸方「未払消費税」</li>
                            <li>事業税: 借方「法人税等」/ 貸方「未払事業税」</li>
                        </ul>
                    </div>
                </div>
            )}

            {activeTab === 'capital-transfer' && isKojin && (
                <div className="card">
                    <h3 style={{marginBottom: '12px'}}>決算振替仕訳（元入金振替）</h3>
                    <p style={{color: '#666', marginBottom: '16px', fontSize: '14px'}}>
                        期末に損益・事業主貸・事業主借を元入金に振り替えます。
                    </p>
                    <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '16px'}}>
                        <tbody>
                            <tr>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee'}}>当期純利益</td>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right'}}>{formatCurrency(netIncome)} 円</td>
                            </tr>
                            <tr>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee'}}>事業主貸（控除）</td>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right'}}>△{formatCurrency(ownerWithdrawal)} 円</td>
                            </tr>
                            <tr>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee'}}>事業主借（加算）</td>
                                <td style={{padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right'}}>{formatCurrency(ownerDeposit)} 円</td>
                            </tr>
                            <tr style={{fontWeight: 'bold'}}>
                                <td style={{padding: '8px'}}>元入金増減額</td>
                                <td style={{padding: '8px', textAlign: 'right'}}>{formatCurrency(netIncome - ownerWithdrawal + ownerDeposit)} 円</td>
                            </tr>
                        </tbody>
                    </table>
                    <button className="btn btn-primary" onClick={handleGenerateCapitalTransfer}>振替仕訳を生成</button>
                    <p style={{fontSize: '12px', color: '#888', marginTop: '8px'}}>既存の元入金振替仕訳がある場合は削除して再生成します。</p>
                </div>
            )}

            {activeTab === 'registered' && (
                <div className="card">
                    <h3>登録済み決算整理仕訳</h3>

                    {existingClosingEntries.length === 0 ? (
                        <p style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                            登録済みの決算整理仕訳はありません
                        </p>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>日付</th>
                                    <th>摘要</th>
                                    <th>借方</th>
                                    <th>貸方</th>
                                    <th style={{ textAlign: 'right' }}>金額</th>
                                </tr>
                            </thead>
                            <tbody>
                                {existingClosingEntries.map(entry => {
                                    const debitLine = entry.lines.find(l => l.type === 'debit');
                                    const creditLine = entry.lines.find(l => l.type === 'credit');
                                    const debitAccount = accounts.find(a => a.id === debitLine?.accountId);
                                    const creditAccount = accounts.find(a => a.id === creditLine?.accountId);
                                    return (
                                        <tr key={entry.id}>
                                            <td>{formatDate(entry.date)}</td>
                                            <td>{entry.description}</td>
                                            <td>{debitAccount?.name}</td>
                                            <td>{creditAccount?.name}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(debitLine?.amount || 0)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    <p style={{ marginTop: '16px', fontSize: '13px', color: '#666' }}>
                        ※ 決算整理仕訳を削除する場合は「仕訳帳」から行ってください
                    </p>
                </div>
            )}
        </div>
    );
};

export default ClosingWizard;
